/**
 * ChartExplainChat — AI explanation of the candlestick chart, rendered
 * directly under the Multi-Benchmark Comparison chart.  Auto-generates a
 * structured explanation (Simple explanation / Overall structure /
 * Consolidation zone / Direction of candles) and supports follow-up
 * questions in the same session.  Fixed-height frame for page stability.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Bot, RefreshCw, Send, CandlestickChart } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API } from "../config/api";

export default function ChartExplainChat({ ticker, timeframe = "1d", interval = "auto" }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const listRef = useRef(null);

  const fetchBarsAndAsk = async (question = null, sid = null) => {
    setBusy(true);
    setErr(null);
    try {
      const params = { timeframe };
      if (interval && interval !== "auto") params.interval = interval;
      const barsRes = await axios.get(`${API}/stock/indicators-chart/${ticker}`, { params, timeout: 35000 });
      const raw = barsRes.data?.chart_data || [];
      const bars = raw.map(b => ({
        o: b.candle_open ?? b.open, h: b.candle_high ?? b.high,
        l: b.candle_low ?? b.low, c: b.candle_close ?? b.price, t: b.time,
      }));
      const r = await axios.post(`${API}/chart-ai/explain`, {
        ticker, timeframe, interval: interval !== "auto" ? interval : null,
        bars, levels: {}, question, session_id: sid,
      }, { timeout: 60000 });
      setSessionId(r.data.session_id);
      setMessages(m => [...m, { role: "ai", text: r.data.answer }]);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Chart explanation failed — try again.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    setMessages([]);
    setSessionId(null);
    if (ticker) fetchBarsAndAsk();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker, timeframe]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = () => {
    const q = input.trim();
    if (!q || busy) return;
    setMessages(m => [...m, { role: "user", text: q }]);
    setInput("");
    fetchBarsAndAsk(q, sessionId);
  };

  return (
    <Card className="bg-slate-900/40 border-slate-700 h-[1050px] flex flex-col mt-3" data-testid="chart-explain-card">
      <CardHeader className="pb-2 shrink-0">
        <CardTitle className="text-base text-white flex items-center gap-2">
          <CandlestickChart className="w-5 h-5 text-orange-400" />
          Chart Explanation — AI
          <span className="text-xs text-slate-400 font-normal ml-auto">reads the candles above · {ticker}</span>
          <button
            type="button"
            onClick={() => { setMessages([]); fetchBarsAndAsk(); }}
            disabled={busy}
            data-testid="chart-explain-refresh"
            className="inline-flex items-center gap-1.5 rounded border border-slate-600 bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-100 hover:bg-slate-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
            Re-analyze
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col min-h-0 space-y-2 overflow-hidden">
        <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1" data-testid="chart-explain-list">
          {messages.map((m, i) => m.role === "user" ? (
            <div key={i} className="flex justify-end">
              <div className="max-w-[85%] rounded-lg bg-purple-800/40 border border-purple-800 px-4 py-2 text-sm text-purple-50">{m.text}</div>
            </div>
          ) : (
            <div key={i} className="rounded-lg bg-slate-950/60 border border-slate-700 px-4 py-3 text-sm text-slate-100 leading-relaxed whitespace-pre-line"
                 data-testid={`chart-explain-answer-${i}`}>
              {m.text.split(/\*\*(.+?)\*\*/g).map((part, j) =>
                j % 2 === 1 ? <span key={j} className="block font-bold text-orange-300 mt-2 first:mt-0">{part}</span> : part
              )}
            </div>
          ))}
          {busy && (
            <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="chart-explain-thinking">
              <RefreshCw className="w-4 h-4 animate-spin" /> analyzing the candles…
            </div>
          )}
          {err && <div className="text-sm text-rose-300 bg-rose-950/40 border border-rose-700 rounded p-3" data-testid="chart-explain-error">{err}</div>}
        </div>
        <div className="flex gap-2 shrink-0">
          <input
            data-testid="chart-explain-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && send()}
            placeholder="Ask about the chart — consolidation, direction, momentum…"
            disabled={busy}
            className="flex-1 rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500"
          />
          <button
            type="button"
            onClick={send}
            disabled={busy || !input.trim()}
            data-testid="chart-explain-send"
            className="inline-flex items-center gap-2 rounded border border-orange-700 bg-orange-800/60 px-4 py-2 text-sm font-semibold text-orange-50 hover:bg-orange-700 disabled:opacity-50 transition-colors"
          >
            <Send className="w-4 h-4" /> Send
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
